import { Plus, Search } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import { getHolidaysTabColumns } from '../../../helpers/datasheetConstants';
import DataTable from '../../common/data-table/DataTable';
import AddHolidayModal from './AddHolidayModal';
import AlertModal from '../../AlertModal';
import moment from 'moment';
import { selectStyles } from '../../../helpers/global';

const yearSelectOptions = [
  { label: 'All', value: 'all' },
  { label: String(moment().year() - 1), value: String(moment().year() - 1) },
  { label: String(moment().year()), value: String(moment().year()) },
  { label: String(moment().year() + 1), value: String(moment().year() + 1) }
];

const HolidaysTab = () => {
  const [selectedYear, setSelectedYear] = useState(yearSelectOptions[0].value);
  const [showAddHolidayModal, setShowAddHolidayModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [editHoliday, setEditHoliday] = useState(null);
  const [holidaySearchQuery, setHolidaySearchQuery] = useState('');
  const [toBeDeletedHoliday, setToBeDeletedHoliday] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    currentPage: 1,
    totalCount: 0,
    totalPages: 1
  });

  const PAGE_SIZE = 10;

  const columns = useMemo(
    () =>
      getHolidaysTabColumns({
        setEditHoliday,
        setShowAddHolidayModal,
        setToBeDeletedHoliday,
        setShowDeleteAlert
      }),
    []
  );

  const fetchHolidays = async (
    yearValue = selectedYear,
    page = paginationMeta.currentPage,
    { showLoading = false } = {}
  ) => {
    if (showLoading) setIsFetchingData(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.HOLIDAYS_LIST,
      method: 'POST',
      requestBody: {
        page,
        pageSize: PAGE_SIZE,
        sort: {
          startDate: 1
        },
        query: holidaySearchQuery.trim() || undefined,
        filter: {
          year: yearValue && yearValue !== 'all' ? yearValue : undefined
        }
      }
    });

    if (error) {
      toast.error(error);
      if (showLoading) setIsFetchingData(false);
      return;
    }

    const rows = response?.results || [];

    setHolidays(rows);

    const total = response?.total ?? rows.length;
    const nextTotalPages =
      typeof response?.totalPages === 'number' && response.totalPages >= 1
        ? response.totalPages
        : Math.max(1, Math.ceil(total / PAGE_SIZE));

    setPaginationMeta(prev => ({
      ...prev,
      totalCount: total,
      totalPages: nextTotalPages,
      currentPage: rows.length === 0 && page > 1 ? Math.max(1, page - 1) : page
    }));

    if (showLoading) setIsFetchingData(false);
  };

  useLayoutEffect(() => {
    setPaginationMeta(prev => ({ ...prev, currentPage: 1 }));
  }, [selectedYear, holidaySearchQuery]);

  useEffect(() => {
    fetchHolidays(selectedYear, paginationMeta.currentPage, { showLoading: true });
  }, [selectedYear, holidaySearchQuery, paginationMeta.currentPage]);

  const handleAddEditHoliday = async values => {
    setLoading(true);

    const requestBody = editHoliday?._id
      ? {
          holidayName: values.holidayName,
          startDate: values.startDate,
          endDate: values.endDate,
          notes: values.notes
        }
      : { holidays: values };

    const { error } = await makeRESTApiRequest({
      endpoint: editHoliday?._id ? ENDPOINTS.HOLIDAYS_WITH_ID(editHoliday?._id) : ENDPOINTS.HOLIDAYS,
      method: editHoliday?._id ? 'PUT' : 'POST',
      requestBody
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    toast.success(editHoliday?._id ? 'Holiday updated successfully' : 'Holidays added successfully');
    setShowAddHolidayModal(false);
    setEditHoliday(null);
    await fetchHolidays(selectedYear, paginationMeta.currentPage);
    setLoading(false);
  };

  const handleDeleteHoliday = async id => {
    setLoading(true);

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.HOLIDAYS_WITH_ID(id),
      method: 'DELETE'
    });
    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }
    toast.success('Holiday deleted successfully');
    await fetchHolidays(selectedYear, paginationMeta.currentPage);
    setLoading(false);
    setShowDeleteAlert(false);
    setToBeDeletedHoliday(null);
  };

  return (
    <div className="mt-3">
      <div
        className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center mb-3"
        style={{ gap: 12 }}
      >
        <div className="position-relative flex-grow-1">
          <Search
            className="icon-md position-absolute"
            style={{
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              color: '#6c757d'
            }}
          />
          <Form.Control
            type="text"
            size="lg"
            className="bg-white search-input text-dark xsmallFont"
            placeholder="Search by holiday name..."
            value={holidaySearchQuery}
            onChange={e => {
              setHolidaySearchQuery(e.target.value);
            }}
            style={{ paddingLeft: 42, border: '1px solid #e5e7ea', fontWeight: 'normal' }}
          />
        </div>
        <div className="flex-shrink-0">
          <Select
            classNamePrefix="select"
            className="basic-single select-organization"
            placeholder="Year"
            options={yearSelectOptions}
            value={yearSelectOptions.find(o => o.value === selectedYear) ?? yearSelectOptions[0]}
            onChange={opt => opt && setSelectedYear(opt.value)}
            isClearable={false}
            styles={selectStyles}
          />
        </div>
        <Button
          variant="success"
          className="d-flex align-items-center"
          onClick={() => {
            setEditHoliday(null);
            setShowAddHolidayModal(true);
          }}
          style={{ minHeight: '40px' }}
        >
          <Plus size={16} className="mr-1" />
          Add Holiday
        </Button>
      </div>

      <div style={{ borderRadius: 12 }}>
        <DataTable
          data={holidays}
          columns={columns}
          rowKey="_id"
          sortable
          pagination
          pageSize={PAGE_SIZE}
          currentPage={paginationMeta.currentPage}
          onPageChange={page =>
            setPaginationMeta(prev => ({
              ...prev,
              currentPage: page
            }))
          }
          totalCount={paginationMeta.totalCount}
          totalPages={paginationMeta.totalPages}
          striped={false}
          hover
          bordered={false}
          responsive={false}
          maxHeight="100%"
          emptyMessage="No holidays found"
          loading={isFetchingData}
        />
      </div>
      <AddHolidayModal
        show={showAddHolidayModal}
        onHide={() => {
          setShowAddHolidayModal(false);
          setEditHoliday(null);
        }}
        onSubmit={handleAddEditHoliday}
        showProgress={loading}
        edit={editHoliday}
      />
      <AlertModal
        show={showDeleteAlert}
        onHide={() => setShowDeleteAlert(false)}
        onContinueClick={() => handleDeleteHoliday(toBeDeletedHoliday)}
        onDismissClick={() => setShowDeleteAlert(false)}
        showProgress={loading}
        alertText="Are you sure you want to delete this holiday?"
        progressText="Deleting holiday..."
      />
    </div>
  );
};

export default HolidaysTab;
